import { useRef } from "react";
import {
  Box,
  Button,
  List,
  ListItem,
  useColorModeValue,
  Drawer,
  DrawerBody,
  DrawerHeader,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  useDisclosure,
  useMediaQuery,
  Heading,
  IconButton,
} from "@chakra-ui/react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context";
import ToggleColor from "./ToggleColor";
import {
  CollecticonExpandTopRight,
  CollecticonHamburgerMenu,
  CollecticonIsoStack,
  CollecticonLogin,
  CollecticonLogout,
  CollecticonMap,
} from "@devseed-ui/collecticons-chakra";

type MenuButtonProps = {
  href?: string;
  children: React.ReactNode;
  as?: React.ElementType;
  target?: string;
  onClick?: () => void;
  icon?: React.ReactNode;
};

function MenuButton({
  children,
  as,
  href,
  target,
  onClick,
  icon,
}: MenuButtonProps) {
  const { pathname } = useLocation();
  const isActive = href ? pathname.startsWith(href) : false;

  return (
    <ListItem
      flex="1"
      borderBottom="2px solid"
      borderBottomColor={isActive ? "primary.500" : "transparent"}
    >
      <Button
        w="full"
        variant={isActive ? "primary-text" : "base-text"}
        fontSize="0.75rem"
        pt="0.25"
        size="sm"
        fontWeight="normal"
        letterSpacing="0.06rem"
        fontFamily="mono"
        textTransform="uppercase"
        justifyContent="flex-start"
        as={as || Link}
        to={!as ? href : undefined}
        href={as === "a" ? href : undefined}
        target={target}
        onClick={onClick}
        gap="2"
        _hover={{
          backgroundColor: useColorModeValue("primary.100a", "primary.200a"),
          color: useColorModeValue("primary.700", "primary.300"),
        }}
        transition="all 0.2s"
      >
        {icon && <Box>{icon}</Box>}
        {children}
      </Button>
    </ListItem>
  );
}

type NavMenuProps = {
  isAuthenticated: boolean;
  isMediumDown: boolean;
  handleLogout: () => void;
};

function NavMenuList({
  isAuthenticated,
  handleLogout,
  isMediumDown,
}: NavMenuProps) {
  return (
    <List
      display="flex"
      flexDirection={isMediumDown ? "column" : "row"}
      gap={isMediumDown ? "4" : "6"}
    >
      {isAuthenticated && (
        <MenuButton href="/scenes" icon={<CollecticonIsoStack size="3" />}>
          My Scenes
        </MenuButton>
      )}
      <MenuButton href="/explore" icon={<CollecticonMap size="3" />}>
        Explore
      </MenuButton>
      <MenuButton
        as="a"
        href="https://docs.whalesai.org"
        target="_blank"
        icon={<CollecticonExpandTopRight size="3" />}
      >
        User Guide
      </MenuButton>
      {isAuthenticated ? (
        <MenuButton onClick={handleLogout} icon={<CollecticonLogin size="3" />}>
          Logout
        </MenuButton>
      ) : (
        <MenuButton href="/login" icon={<CollecticonLogout size="3" />}>
          Log In
        </MenuButton>
      )}
    </List>
  );
}

function GlobalPageHeader() {
  const bg = useColorModeValue("white", "base.500");
  const linkColor = useColorModeValue("primary.700", "primary.400");
  const navigate = useNavigate();
  const { isAuthenticated, logout } = useAuth();
  const [isMediumDown] = useMediaQuery("(max-width: 800px)");
  const { isOpen, onOpen, onClose } = useDisclosure();
  const btnRef = useRef<HTMLButtonElement>(null);
  const handleLogout = () => {
    logout();
    navigate("/");
  };

  return (
    <Box
      as="header"
      display="flex"
      alignItems="center"
      gap="5"
      bg={bg}
      px="5"
      py="2"
      shadow="base"
    >
      <Heading
        fontFamily="mono"
        letterSpacing="0.125rem"
        fontWeight="400"
        textTransform="uppercase"
        size="md"
        as="h1"
        color={linkColor}
        _hover={{
          backgroundColor: useColorModeValue("primary.100a", "primary.200a"),
          color: useColorModeValue("primary.700", "primary.300"),
        }}
        transition="all 0.2s"
        mr="auto"
      >
        <Link to="/">WhalesAI.org</Link>
      </Heading>
      <ToggleColor />
      {!isMediumDown ? (
        <Box as="nav">
          <NavMenuList
            isAuthenticated={isAuthenticated}
            handleLogout={handleLogout}
            isMediumDown={isMediumDown}
          />
        </Box>
      ) : (
        <>
          <IconButton
            ref={btnRef}
            size="sm"
            variant="primary-text"
            onClick={onOpen}
            aria-label="Open mobile menu"
            icon={<CollecticonHamburgerMenu />}
          />
          <Drawer isOpen={isOpen} placement="right" onClose={onClose}>
            <DrawerOverlay />
            <DrawerContent>
              <DrawerCloseButton />
              <DrawerHeader
                as="h1"
                letterSpacing="0.125rem"
                fontWeight="400"
                fontFamily="mono"
                textTransform="uppercase"
                color={linkColor}
              >
                WhalesAI.org
              </DrawerHeader>

              <DrawerBody as="nav">
                <NavMenuList
                  isAuthenticated={isAuthenticated}
                  handleLogout={handleLogout}
                  isMediumDown={isMediumDown}
                />
              </DrawerBody>
            </DrawerContent>
          </Drawer>
        </>
      )}
    </Box>
  );
}

export default GlobalPageHeader;
